import { useState, useEffect } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { getAuthUser } from "@/lib/authUser";

interface UserAccountData {
  accountId: string | null;
  userId: string | null;
  role: string | null;
  isLoading: boolean;
}

export const useUserAccount = (): UserAccountData => {
  const [accountId, setAccountId] = useState<string | null>(null);
  const [userId, setUserId] = useState<string | null>(null);
  const [role, setRole] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    const fetchAccountData = async () => {
      try {
        const { data: { user } } = await getAuthUser();
        
        if (!user) {
          setIsLoading(false);
          return;
        }

        setUserId(user.id);

        // Get the user's primary account
        const { data: accountUser, error } = await supabase
          .from('account_users')
          .select('account_id, role')
          .eq('user_id', user.id)
          .maybeSingle();

        if (accountUser) {
          setAccountId(accountUser.account_id);
          setRole(accountUser.role);
        }
      } catch (err) {
        console.error('Error fetching account data:', err);
      } finally {
        setIsLoading(false);
      }
    };

    fetchAccountData();

    // Subscribe to auth state changes
    const { data: { subscription } } = supabase.auth.onAuthStateChange((_event, session) => {
      if (!session?.user) {
        setAccountId(null);
        setUserId(null);
        setRole(null);
      } else {
        fetchAccountData();
      }
    });

    return () => subscription.unsubscribe();
  }, []);

  return { accountId, userId, role, isLoading };
};
